import { Grid } from '@mui/material';
import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  ActivityLogSearchResponse,
  ICandidate,
  InformationReqResponse,
  getAllResponses,
  getInformationReqTemplatesByJob,
  getSubmissionsByCandidateIdAndJobId,
  updateRTRfeedback,
} from 'wisestep-api';
import { getHoursOld, ScrollableTabs, TabTextVariant, WsColors } from 'wisestep-base';

import { useCandidateViewData } from '../../../../../features/candidateLayout/utils/CandidateViewContext';
import { CandidateAtsAccess, useCandidateAts } from '../../../../../hooks/useCandidateAts';
import {
  getSubmissionLayoutTab,
  SUBMISSION_LAYOUT_TABS,
  submissionLayoutTabNames,
  submissionLayoutTabNamesWithoutPotentialRevenueTab,
} from '../../../utils/helper';
import TimeLine from '../../activityLogs/components/timeLine/TimeLIne';
import CandidateEvaluation from '../../childComponets/CandidateEvaluation/CandidateEvaluation';
import { CandidateEvent } from '../../childComponets/CandidateEvent/CandidateEvent';
import { CandidateInformationRequest } from '../../childComponets/CandidateInformationRequest/CandidateInformationRequest';
import CandidateResume from '../../childComponets/CandidateResume';
import CandidateRevenue from '../../childComponets/PotentialRevenue';
import NoPreview from '../../documents/components/NoPreview';
import MappingBanner from '../../mappings/MappingBanner';
import RTRRequestMappingStatus from '../../mappings/RTRRequestMappingStatus';

import DocumentListDisplay from './DocumentListDisplay';
import SubmissionDetails from './SubmissionDetails';

interface SubmissionLayoutProps {
  candidate?: ICandidate | undefined;
  jobId;
}

interface Note {
  notes: string;
  createdAt: string | Date;
  createdBy: string;
}

const SubmissionLayout = ({ jobId }: SubmissionLayoutProps) => {
  const [params] = useSearchParams();
  const [interaction, setCandidate] = useState<ICandidate>();
  const { candidateViewData } = useCandidateViewData();
  const [evalTemplateId, setEvalTemplateId] = useState(null);
  const [flag, setFlag] = useState();
  const isPotentialRevenueTabVisible = useCandidateAts(
    CandidateAtsAccess.POTENTIAL_REVENUE_VISIBILITY
  );
  const list = isPotentialRevenueTabVisible
    ? submissionLayoutTabNames
    : submissionLayoutTabNamesWithoutPotentialRevenueTab;

  const tabType = (params.get('tab') as SUBMISSION_LAYOUT_TABS) ?? '';
  const [index, setIndex] = useState<number>(getSubmissionLayoutTab(tabType, list));
  const [activities, setActivities] = useState<ActivityLogSearchResponse[]>([]);
  const selectedIndex = (index: number) => setIndex(index);
  const [getRTRTemplateData, setRTRTemplateData] = useState<InformationReqResponse>();
  sessionStorage.removeItem('sessionCandidateProfileRedirectUrlCorpHiring');
  const corphiringTemplate = params.get('corphiringTemplate');
  useEffect(() => {
    if (corphiringTemplate) {
      if (corphiringTemplate === 'informationrequestTemplate') {
        setIndex(3);
      } else if (corphiringTemplate === 'evalutionTemplate') {
        setIndex(4);
      }
    }
  }, [corphiringTemplate]);

  useEffect(() => {
    (async () => {
      setActivities([]);
      if (candidateViewData?.candidateId && candidateViewData?.workspaceId) {
        try {
          const submissionData = await getSubmissionsByCandidateIdAndJobId(
            candidateViewData?.candidateId,
            jobId
          );
          setCandidate(submissionData.data?.jobCandidateInteraction);
          const responseActivity = submissionData.data?.activities;
          if (responseActivity && responseActivity.length > 0) {
            const updatedActivities = [...responseActivity];
            if (updatedActivities[0]?.activities?.[0]) {
              updatedActivities[0].activities[0].notes = getLatestNote(
                submissionData.data?.jobCandidateInteraction?.candidateNotes
              );
            }
            setActivities(updatedActivities);
          } else {
            setActivities(submissionData.data?.activities);
          }
          getRTRMappingStatusData(submissionData.data?.jobCandidateInteraction);
        } catch (e) {
          console.log(e);
        }
      }
    })();
  }, [candidateViewData?.candidateId, candidateViewData?.workspaceId, jobId]);

  const getRTRMappingStatusData = async (interaction: ICandidate) => {
    try {
      await getInformationReqTemplatesByJob(interaction.jobId ?? '');

      const responsesResponse = await getAllResponses(
        `${interaction.candidateId}_${interaction.jobId}`,
        interaction.jobId as string,
        interaction.recruiterId ?? ''
      );

      let RtrData = responsesResponse.data.filter(
        ({ informationReqTemplate }) => informationReqTemplate.rtrTemplate
      );
      if (RtrData.length > 0) {
        if (RtrData?.[0]?.rtrFeedback === 'RTR Requested') {
          const expiryHours = getHoursOld(RtrData?.[0]?.requestedOn);
          if (expiryHours > 72) {
            const responseData = await updateRTRfeedback({
              id: RtrData?.[0]?.id,
              rtrFeedback: 'RTR Expired',
            });
            if (responseData) RtrData = [responseData?.data];
          }
        }
      }
      setRTRTemplateData(RtrData?.[0]);
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const getLatestNote = (notes: Note[]): string | null => {
    if (!notes || notes.length === 0) return null;
    const sortedNotes = [...notes].sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
    const notesReq = sortedNotes[0]?.notes;
    return (
      notesReq
        ?.replace(/<\/?strong>/g, '')
        .replace(/Invalid Date IST - UTC \+05:30/, '')
        .trim() || null
    );
  };

  const updateInteraction = (interaction: ICandidate) => setCandidate(interaction);

  return (
    <>
      <Grid container sx={{ backgroundColor: WsColors.White }} mt={1} px={2} py={2.2} ml={1.2}>
        {interaction && (
          <>
            <Grid item xs={12}>
              <MappingBanner
                interaction={interaction}
                updateInteraction={updateInteraction}
                isSubmissions={true}
              />
            </Grid>
            <Grid item xs={12} mb={1}>
              <ScrollableTabs
                scrollList={list}
                getTabIndex={selectedIndex}
                variant={TabTextVariant.SUB_TITLE}
                defaultValue={index}
                visibleScrollbar={true}
                scrollButtons={false}
              />
            </Grid>
            <Grid item xs={12}>
              {index == 0 && (
                <Grid item xs={12} minHeight={550}>
                  {activities?.map((activity) => (
                    <TimeLine
                      key={`${String(activity.date)}-${candidateViewData?.candidateId}`}
                      onclick={() => {}}
                      activity={activity}
                    />
                  ))}
                </Grid>
              )}
              {index === 1 && <SubmissionDetails key={interaction?.id} submission={interaction} />}
              {index === 2 && (
                <CandidateEvent
                  candidate={interaction}
                  setTabIndex={setIndex}
                  setEvalTemplateId={setEvalTemplateId}
                  setFlag={setFlag}
                />
              )}
              {index === 4 && (
                <CandidateEvaluation
                  candidiateId={interaction.id}
                  currentJobCode={interaction.jobCode}
                  templateId={evalTemplateId}
                  candidiateData={interaction}
                  flag={flag}
                />
              )}
              {index === 3 && (
                <>
                  {getRTRTemplateData && (
                    <RTRRequestMappingStatus
                      initialRTRData={getRTRTemplateData}
                      candidateInteractionData={interaction}
                      isMapping={false}
                      selectedJob={jobId ?? ''}
                    />
                  )}
                  <CandidateInformationRequest candidate={interaction} />
                </>
              )}
              {index === 5 && (
                <CandidateResume
                  job={null}
                  resumeLocation={interaction.resumeLocation ?? ''}
                  pdfResumeUrl={interaction.resumeLocation ?? ''}
                />
              )}
              {index === 6 && (
                <>
                  {interaction?.otherDocuments?.length ? (
                    <DocumentListDisplay
                      key={interaction?.id}
                      documents={interaction?.otherDocuments ?? []}
                    />
                  ) : (
                    <NoPreview text={'CV/Document upload'} />
                  )}
                </>
              )}
              {index === 7 && (
                <CandidateRevenue
                  isPotentialRevenueTabVisible={isPotentialRevenueTabVisible}
                  jobId={jobId}
                  candidateInteraction={interaction}
                />
              )}
            </Grid>
          </>
        )}
      </Grid>
    </>
  );
};

export default SubmissionLayout;
